import '@justgiving/loading/src/Loading.scss';

import React, { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { NetworkStatus, gql } from '@apollo/client';

import { SupportersListQuery } from '../../../types/__generated__';
import { SIZE_136_136 } from '../../constants/imageTemplateStrings';
import useFullPageQuery from '../../hooks/useFullPageQuery';
import { makePhotoUrl } from '../../utils/makePhotoUrl';
import DonateButton from '../DonateButton/DonateButton';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import PageSection from '../PageSection/PageSection';
import SupporterDetails from '../SupporterDetails/SupporterDetails';
import SupporterDetailsPlaceholder from '../SupporterDetails/SupporterDetailsPlaceholder';
import SupportersListSkeleton from './SupportersListSkeleton';

import styles from './SupportersList.scss';

export const DOCUMENT = gql`
  query SupportersList(
    $after: String
    $type: PageType!
    $slug: Slug!
    $preview: Preview
  ) {
    page(type: $type, slug: $slug, preview: $preview) {
      id
      donationSummary {
        donationCount
      }
      donations(first: 5, after: $after) {
        edges {
          node {
            id
            legacyId
            creationDate
            amount {
              value
              currencyCode
            }
            giftAidAmount {
              value
              currencyCode
            }
            displayName
            avatar
            message
          }
        }
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

type SupportersListProps = {
  noSupportersMessage: string;
  displayDonateButton?: boolean;
  donateButtonExperiments?: string[];
};

const SupportersList: FunctionComponent<SupportersListProps> = ({
  noSupportersMessage,
  displayDonateButton,
  donateButtonExperiments,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });

  const { error, data, fetchMore, refetch, networkStatus } = useFullPageQuery<
    SupportersListQuery
  >(DOCUMENT, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  if (
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.refetch
  ) {
    return <SupportersListSkeleton animated displayRefreshButton={false} />;
  }

  if (error) {
    return <SupportersListSkeleton onRefresh={refetch} />;
  }

  if (!data?.page?.donations) {
    return null;
  }

  const {
    page: {
      donationSummary,
      donations: {
        edges,
        pageInfo: { hasNextPage, endCursor },
      },
    },
  } = data;

  async function handleFetchMore() {
    await fetchMore({
      variables: { after: endCursor },
    });
  }

  return (
    <PageSection
      title={
        donationSummary?.donationCount === 0
          ? t('supporters.noSupporters', 'No supporters yet')
          : t('supporters.title', 'Supporters')
      }
      count={donationSummary?.donationCount ?? 0}
      className={styles.container}
    >
      <ul data-qa="supporters-list">
        {(donationSummary?.donationCount ?? 0) > 0 &&
          edges.map(supporter => {
            const {
              avatar,
              amount,
              giftAidAmount,
              message,
              creationDate,
              displayName,
              legacyId,
            } = supporter.node;
            return (
              <SupporterDetails
                key={legacyId}
                image={makePhotoUrl(avatar, SIZE_136_136)}
                message={message}
                currencyCode={
                  amount && amount.currencyCode ? amount.currencyCode : 'GBP'
                }
                donationAmount={amount && amount.value ? amount.value / 100 : 0}
                giftAidAmount={
                  giftAidAmount && giftAidAmount.value
                    ? giftAidAmount.value / 100
                    : 0
                }
                date={creationDate}
                userName={displayName}
              />
            );
          })}

        {(donationSummary?.donationCount ?? 0) === 0 && (
          <>
            <p className="jg-space-mbn" data-qa="no-supporters">
              {noSupportersMessage}
            </p>
            <SupporterDetailsPlaceholder />
          </>
        )}
      </ul>

      {hasNextPage && (
        <LoadMoreButton
          className={styles.button}
          loading={networkStatus === NetworkStatus.fetchMore}
          onClick={handleFetchMore}
          trackingData={{
            action_name: 'click',
            subtype: 'button',
            event: 'click',
            event_value: 'show more',
            sub_section: 'supporters',
          }}
        >
          <Trans i18nKey="supporters.showMore">
            Show more
            <span className="jg-sr-only"> supporters</span>
          </Trans>
        </LoadMoreButton>
      )}

      {displayDonateButton && (
        <DonateButton
          className={styles.button}
          inactivePageBehaviour="hide"
          trackingDataPageSection="SupportersList"
          experiments={donateButtonExperiments}
          successType="SupportersDonateButtonClick"
        />
      )}
    </PageSection>
  );
};

export default SupportersList;
